import { useMemo } from 'react';
import useCalendarDataOther from './useCalendarDataOther';
import { processCalendarData, addPredictions } from '../data/cycle-predictions/processCalendarData';

const TAG = '[useCalendarDataPredictedOtherUser]';

/**
 * Hook to load and process another user's calendar data with cycle predictions.
 *
 * This hook:
 * - Uses useCalendarDataOther to fetch raw calendar data via RPC
 * - Processes the data with cycle day calculations (processCalendarData)
 * - Extends predictions until the given end date (addPredictions)
 * - Does NOT use Redux (no caching, no optimistic updates)
 * - Does NOT update the database
 * - Returns processed calendar data with predictions and loading state
 *
 * @param {string} userId - The target user's ID to fetch calendar data for
 * @param {string} startMonth - Start month in YYYY-MM format (e.g. '2026-02')
 * @param {string} endMonth - End month in YYYY-MM format (e.g. '2026-08')
 * @param {string} predictionEndDate - End date in YYYY-MM-DD format to extend predictions to
 * @returns {{ calendarData: object, loading: boolean, refresh: function }}
 */
export default function useCalendarDataPredictedOtherUser(userId, startMonth = null, endMonth = null, predictionEndDate = null) {
  const { calendarData: rawCalendarData, loading, refresh } = useCalendarDataOther(userId, startMonth, endMonth);

  // Process calendar data with predictions
  const calendarData = useMemo(() => {
    const processed = processCalendarData(rawCalendarData);
    const withPredictions = addPredictions(processed, predictionEndDate);
    return withPredictions;
  }, [rawCalendarData, predictionEndDate, userId]);

  return { calendarData, loading, refresh };
}
